// src/ui/components/syncStatusBadge.ts — Nautical Sync Status Emblem for Shell Navigation

import { tokens } from '../designTokens';
import { SyncEngine } from '../../storage/syncEngine';
import type { SyncStatusState } from '../../storage/syncTypes';

export class SyncStatusBadge {
  private element: HTMLElement;
  private unsubscribe?: () => void;

  constructor() {
    this.element = document.createElement('button');
    this.element.id = 'sync-status-badge';
    this.element.className = 'touch-target';
    this.element.setAttribute('aria-label', 'Cloud Synchronization Status');
    this.render(SyncEngine.getStatus());

    this.unsubscribe = SyncEngine.onStatusChange((status) => {
      this.render(status);
    });

    this.element.addEventListener('click', () => {
      SyncEngine.syncNow().catch(() => {});
    });
  }

  getElement(): HTMLElement {
    return this.element;
  }

  destroy(): void {
    if (this.unsubscribe) {
      this.unsubscribe();
    }
  }

  private render(status: SyncStatusState): void {
    let icon = '⚓';
    let text = 'Synced';
    let color: string = tokens.color.seaweedSage;
    let title = 'All field journals & metrics synchronized';

    switch (status) {
      case 'syncing':
        icon = '🔄';
        text = 'Syncing...';
        color = tokens.color.gildedBrass;
        title = 'Synchronizing logbook with Cloudflare D1...';
        break;
      case 'offline':
        icon = '📡';
        text = 'Cabin Mode';
        color = tokens.color.vellumEdge;
        title = 'Offline: All data saved locally on tablet';
        break;
      case 'error':
        icon = '⚠️';
        text = 'Tap to Retry';
        color = tokens.color.madderRed;
        title = 'Sync issue: Tap to attempt reconnection';
        break;
      case 'synced':
      default:
        icon = '⚓';
        text = 'Synced';
        color = tokens.color.seaweedSage;
        title = 'Connected: All field records synchronized';
        break;
    }

    this.element.title = title;
    this.element.style.cssText = `
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 6px 12px;
      background: rgba(250, 248, 245, 0.9);
      border: 1.5px solid ${color};
      border-radius: 12px;
      font-size: 0.8rem;
      font-weight: 700;
      color: ${tokens.color.morrisIndigo};
      cursor: pointer;
      box-shadow: 0 2px 6px rgba(7, 20, 36, 0.08);
      transition: all 0.2s ease;
    `;

    this.element.innerHTML = `
      <span style="font-size: 0.95rem; ${status === 'syncing' ? 'display:inline-block; animation: spin 1s linear infinite;' : ''}">${icon}</span>
      <span>${text}</span>
    `;
  }
}
